import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Snackbar,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import WTypography from '../../../Components/whiteTypography'
import ConseillerChat from '../../ConseillerPatrimonial/components/ConseillerChat'
import { loadWallet } from '../../../Components/Billing/billingThunks'
import { makePostRequest } from 'utils/api'

import { buildSocialAdvisor } from '../utils/buildSocialAdvisor'
import { fetchSocialAdvisorSources } from '../utils/fetchSocialAdvisorSources'
import {
  SocialScoreCard,
  SocialDiagnosticPanel,
  SocialActionsPanel,
  SocialBreakdownPanel,
  SocialRecommendationPanel,
} from './SocialAdvisorSections'

/**
 * LAP-768 — Page principale "Mon conseiller social / relationnel IA".
 *
 * Pattern : strict miroir de `ConseillerProPage` (LAP-767).
 *   - On agrège l'agrégat de /api/Social/dashboard/ (overview, répartition,
 *     évolution, relances suggérées, insights).
 *   - On passe l'agrégat à `buildSocialAdvisor` qui produit score / diagnostic /
 *     actions / répartition / recommandations.
 *   - On expose un chat (réutilisation directe de `ConseillerChat`) pour les
 *     questions libres.
 *
 * Route : /conseiller-social (cf. App.js).
 */
function ConseillerSocialPage() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation('layout')

  const [chatOpen, setChatOpen] = useState(true)
  const [snackbar, setSnackbar] = useState(null)
  const [initialLoading, setInitialLoading] = useState(true)
  const [regenerating, setRegenerating] = useState(false)
  const [lastUpdate, setLastUpdate] = useState(new Date())

  const [rawSources, setRawSources] = useState(null)
  const siteUser = useSelector((s) => s.user)

  // Recalcul de l'advisor à chaque changement de sources OU de langue.
  const advisorData = useMemo(() => {
    if (!rawSources) return null
    return buildSocialAdvisor({ ...rawSources, t })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawSources, t, i18n.language])

  const displayedFirstName =
    advisorData?.userProfile?.firstName ||
    siteUser?.user?.first_name ||
    siteUser?.user?.firstName ||
    siteUser?.user?.prenom ||
    ''

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [])

  useEffect(() => {
    fetchAllSources()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * LAP-768 — Agrégation des sources Life du conseiller social via
   * `fetchSocialAdvisorSources` (endpoints isolés, jamais de throw). Si une
   * source manque, l'advisor produit un diagnostic partiel mais exploitable.
   */
  const fetchAllSources = async ({ forceRefresh = false } = {}) => {
    if (forceRefresh) setRegenerating(true)
    else setInitialLoading(true)

    try {
      const sources = await fetchSocialAdvisorSources(siteUser?.user || {})
      setRawSources(sources)
      setLastUpdate(new Date())

      if (forceRefresh) {
        try {
          await dispatch(loadWallet())
        } catch (_) {
          /* silencieux */
        }
        setSnackbar({
          severity: 'success',
          message: t('conseillerSocial.regenerateSuccess', {
            defaultValue: 'Analyse relationnelle actualisée.',
          }),
        })
      }
    } catch (e) {
      console.warn('[ConseillerSocial] fetch error', e?.message)
      if (!rawSources) {
        setRawSources({ user: siteUser?.user || {}, dashboard: {} })
      }
      if (forceRefresh) {
        setSnackbar({
          severity: 'error',
          message: t('conseillerSocial.regenerateError', {
            defaultValue: 'Impossible d’actualiser l’analyse pour le moment.',
          }),
        })
      }
    } finally {
      if (forceRefresh) setRegenerating(false)
      else setInitialLoading(false)
    }
  }

  const handleRegenerate = () => {
    if (regenerating || initialLoading) return
    fetchAllSources({ forceRefresh: true })
  }

  /**
   * LAP-768 — Envoi d'un message au conseiller social.
   * CONTRAT BACKEND attendu :
   *   POST /api/Social/social-advisor/chat/
   *     body: { message, locale, feature: 'social_chat', context }
   *     output: { reply, source, locale, generated_at }
   * Tant que l'endpoint n'existe pas, on relaie le rejet à ConseillerChat qui
   * utilise sa réponse de démo.
   */
  const handleChatSend = async (message, locale) => {
    try {
      const response = await makePostRequest(
        '/api/Social/social-advisor/chat/',
        {
          message,
          locale,
          feature: 'social_chat',
          context: advisorData
            ? {
                totals: advisorData.totals,
                score: advisorData.socialScore.global,
                topRisks: advisorData.diagnostic.risks.map((r) => r.key),
              }
            : null,
        },
        {},
        true,
      )
      const data = response?.data || {}
      try {
        await dispatch(loadWallet())
      } catch (_) {
        /* silencieux */
      }
      return {
        role: 'agent',
        content: data.reply || data.content || data.text || '',
        source: data.source || t('dashboard.lifeAgentSignature'),
        timestamp: data.generated_at || new Date().toISOString(),
        locale: data.locale || locale,
      }
    } catch (e) {
      console.warn('[ConseillerSocial] endpoint chat indispo, fallback démo', e?.message)
      throw e
    }
  }

  if (initialLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #050b18 0%, #0a162e 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 3,
          px: 4,
          textAlign: 'center',
        }}
      >
        <CircularProgress size={48} sx={{ color: '#f472b6' }} />
        <Box>
          <WTypography variant="h6" sx={{ fontWeight: 700 }}>
            {t('conseillerSocial.initialLoading', {
              defaultValue: 'Votre conseiller analyse votre vie relationnelle…',
            })}
          </WTypography>
          <WTypography variant="body2" sx={{ color: '#f3b3d4 !important', mt: 1, maxWidth: 480 }}>
            {t('conseillerSocial.initialLoadingHint', {
              defaultValue:
                'On agrège votre activité, l’entretien de vos relations clés, la diversité de votre réseau, votre équilibre pro / perso et votre dynamique.',
            })}
          </WTypography>
        </Box>
      </Box>
    )
  }

  const initialChatMessages = [
    {
      role: 'agent',
      content: t('conseillerSocial.chat.welcome', {
        defaultValue: `Bonjour ${displayedFirstName || ''} 🤝 Je suis votre conseiller relationnel. Posez-moi n’importe quelle question sur vos relations, qui recontacter et comment entretenir vos cercles.`,
        name: displayedFirstName,
      }),
      source: t('dashboard.lifeAgentSignature'),
      timestamp: new Date().toISOString(),
    },
  ]

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #050b18 0%, #0a162e 100%)',
        color: 'white',
        pb: 6,
      }}
    >
      {/* Top bar */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backdropFilter: 'blur(12px)',
          bgcolor: 'rgba(5,11,24,0.85)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1, md: 2 },
            px: { xs: 1, sm: 2, md: 3 },
          }}
        >
          <IconButton
            onClick={() => navigate('/social-dashboard')}
            sx={{ color: '#f7c8e0', flexShrink: 0 }}
            aria-label="back"
          >
            <ArrowBackIcon />
          </IconButton>
          <Box flex={1} minWidth={0}>
            <WTypography
              variant="h6"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
                fontSize: { xs: 14, sm: 16, md: 20 },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {t('conseillerSocial.pageTitle', {
                defaultValue: 'Mon conseiller relationnel',
              })}
            </WTypography>
            <WTypography
              variant="caption"
              sx={{ color: '#f3b3d4 !important', display: { xs: 'none', md: 'block' } }}
            >
              {t('conseillerSocial.lastUpdated', {
                defaultValue: `Mis à jour ${formatRelativeI18n(lastUpdate, t)}`,
                date: formatRelativeI18n(lastUpdate, t),
              })}
            </WTypography>
          </Box>

          <IconButton
            onClick={handleRegenerate}
            disabled={regenerating}
            sx={{
              color: '#f7c8e0',
              flexShrink: 0,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
              '&.Mui-disabled': { color: 'rgba(247,200,224,0.4)' },
            }}
            aria-label={t('conseillerSocial.regenerate', {
              defaultValue: 'Actualiser l’analyse',
            })}
            title={t('conseillerSocial.regenerate', {
              defaultValue: 'Actualiser l’analyse',
            })}
          >
            <RestartAltIcon
              sx={{
                animation: regenerating ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          </IconButton>

          <IconButton
            onClick={() => setChatOpen(!chatOpen)}
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              flexShrink: 0,
              bgcolor: chatOpen ? '#f472b6' : 'transparent',
              color: chatOpen ? '#050b18' : '#f7c8e0',
              border: chatOpen ? 'none' : '1px solid rgba(255,255,255,0.2)',
              '&:hover': {
                bgcolor: chatOpen ? '#ec5fa6' : 'rgba(255,255,255,0.06)',
              },
            }}
            aria-label={
              chatOpen
                ? t('conseillerSocial.hideChat', { defaultValue: 'Masquer le chat' })
                : t('conseillerSocial.openChat', { defaultValue: 'Ouvrir le chat' })
            }
          >
            <ChatBubbleOutlineIcon />
          </IconButton>

          <Button
            startIcon={<ChatBubbleOutlineIcon />}
            onClick={() => setChatOpen(!chatOpen)}
            variant={chatOpen ? 'contained' : 'outlined'}
            sx={{
              display: { xs: 'none', md: 'inline-flex' },
              textTransform: 'none',
              fontWeight: 600,
              flexShrink: 0,
              ...(chatOpen
                ? { bgcolor: '#f472b6', color: '#050b18', '&:hover': { bgcolor: '#ec5fa6' } }
                : { color: '#f7c8e0', borderColor: 'rgba(255,255,255,0.2)' }),
            }}
          >
            {chatOpen
              ? t('conseillerSocial.hideChat', { defaultValue: 'Masquer le chat' })
              : t('conseillerSocial.openChat', { defaultValue: 'Ouvrir le chat' })}
          </Button>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ pt: 3 }}>
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: '1fr',
            lg: chatOpen ? '2fr 1fr' : '1fr',
          }}
          gap={3}
          alignItems="flex-start"
        >
          <Box display="flex" flexDirection="column" gap={3}>
            {advisorData && (
              <>
                <SocialScoreCard
                  score={advisorData.socialScore}
                  totals={advisorData.totals}
                  userFirstName={displayedFirstName}
                />
                <SocialRecommendationPanel recommendation={advisorData.recommendation} />
                <SocialDiagnosticPanel
                  diagnostic={advisorData.diagnostic}
                  recommendations={advisorData.recommendations}
                />
                <SocialBreakdownPanel breakdown={advisorData.breakdown} />
                <SocialActionsPanel actions={advisorData.actionsPrioritaires} />
              </>
            )}
          </Box>

          {chatOpen && (
            <Box
              sx={{
                position: { lg: 'sticky' },
                top: { lg: 90 },
                alignSelf: 'flex-start',
                minWidth: 0,
              }}
            >
              <ConseillerChat
                messages={initialChatMessages}
                suggestedPrompts={advisorData?.suggestedPrompts || []}
                userLocale={i18n.language || 'fr-FR'}
                agentSignature={t('dashboard.lifeAgentSignature')}
                onSend={handleChatSend}
              />
            </Box>
          )}
        </Box>
      </Container>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert
            onClose={() => setSnackbar(null)}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  )
}

function formatRelativeI18n(date, t) {
  if (!date) return ''
  const now = Date.now()
  const then = date instanceof Date ? date.getTime() : new Date(date).getTime()
  const diff = Math.max(0, now - then)
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  if (minutes < 1)
    return t('conseillerSocial.relativeTime.justNow', { defaultValue: 'à l’instant' })
  if (minutes < 60)
    return t('conseillerSocial.relativeTime.minutesAgo', {
      defaultValue: `il y a ${minutes} min`,
      n: minutes,
    })
  if (hours < 24)
    return t('conseillerSocial.relativeTime.hoursAgo', {
      defaultValue: `il y a ${hours} h`,
      n: hours,
    })
  return t('conseillerSocial.relativeTime.daysAgo', {
    defaultValue: `il y a ${days} j`,
    n: days,
  })
}

export default ConseillerSocialPage
